// THE THREAD — everything HubSpot holds on one lead, in the order it happened.
//
// The follow-up card tells Bo a touch is due. This answers the question he asks
// next: what has actually passed between us and this company? Every email in
// both directions, every note somebody typed, every call and meeting logged,
// every task raised, every deal attached — merged into one column, oldest at
// the top, newest at the bottom, the way a conversation reads.
//
// READ-ONLY, AND ONLY THE MIRROR. This renders the read-only HubSpot mirror
// (GET /api/hubspot/activity?company_id=…), which is one query over the
// association pointers, so every family comes back already sorted. Nothing here
// writes, and nothing is invented: a body the mirror truncated says so, a
// missing lane says so, and a company that never matched a CRM record gets a
// plain "no record" rather than an empty timeline that reads like "nothing ever
// happened".
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeftIcon,
  MailIcon,
  PhoneIcon,
  CalendarIcon,
  CheckSquareIcon,
  DealFlowIcon,
  TextIcon,
  ClockIcon,
} from './icons.jsx'
import { gmailMessages } from '../lib/gmail.js'
import { rowAddresses } from '../lib/sentMail.js'
import { normDomain } from '../lib/hubspot.js'
import { hubspotActivity, formatDayTime, humanizeEnum } from '../lib/hubspot.js'

// How much history one lead may pull. The route caps at 200; a lead with more
// than that is rare and the view says the older end is missing rather than
// pretending the story starts there.
const LIMIT = 200

// The same stack the drafts are written and copied in, so an email reads here
// exactly as it did in the inbox.
const EMAIL_FONT = { fontFamily: 'Arial, Helvetica, sans-serif' }

const clean = (v) => String(v ?? '').trim()

// HubSpot stores note and email bodies as HTML ('<div dir="auto"><p>Spoke to
// Mike…'), so the raw value renders as a wall of tags. It is also third-party
// text — anyone who emails the connected inbox can choose it — so it is turned
// into PLAIN TEXT and rendered as text, never injected as markup. Block-level
// tags become line breaks first so paragraphs survive the strip.
const ENTITIES = {
  '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'",
  '&apos;': "'", '&nbsp;': ' ',
}
function plainText(value) {
  const raw = String(value ?? '')
  if (!raw) return ''
  if (!/[<&]/.test(raw)) return raw.trim()
  return raw
    .replace(/<\s*(br|\/p|\/div|\/li|\/tr|\/h[1-6])\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&#(\d+);/g, (_, n) => {
      const code = Number(n)
      return Number.isFinite(code) && code > 8 && code < 0x10000 ? String.fromCharCode(code) : ' '
    })
    .replace(/&[a-z#0-9]+;/gi, (m) => ENTITIES[m.toLowerCase()] ?? ' ')
    .replace(/[ \t\u00a0\u2002-\u200b]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((l) => l.trim())
    .join('\n')
    .trim()
}

// The moment an item belongs at. Engagements carry their own occurrence time
// (an email logged today can describe a send from last week); everything else
// is placed by when it was created, which is when it entered the story.
// THE ONE VALUE that both places an item in the story and labels it. They used
// to differ: a task sorted by its due date but printed its created date, so it
// sat at the bottom of the timeline wearing a date three weeks earlier.
export function momentSourceOf(item) {
  return (
    clean(item.occurred_at) ||
    (item.object_type === 'task' ? clean(item.due_at) : '') ||
    clean(item.created_at) ||
    clean(item.updated_at)
  )
}

function momentOf(item) {
  const raw = momentSourceOf(item)
  const n = Number(raw)
  // The mirror stores HubSpot's epoch-milliseconds for engagement timestamps
  // and ISO for the rest; both have to sort together.
  const ms = Number.isFinite(n) && raw && /^\d+$/.test(raw) ? n : Date.parse(raw)
  // NULL, not 0. An item the mirror carries no date for cannot be placed in the
  // story, and 0 placed it at the top as the oldest thing that ever happened —
  // a date this view invented. Undated items sort to the end and say so.
  return Number.isFinite(ms) ? ms : null
}

const KIND = {
  email:   { icon: MailIcon,        label: 'Email',   tone: 'text-primary-dark bg-primary-tint' },
  note:    { icon: TextIcon,        label: 'Note',    tone: 'text-dim bg-panel' },
  call:    { icon: PhoneIcon,       label: 'Call',    tone: 'text-[#a16207] bg-[#fefce8] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]' },
  meeting: { icon: CalendarIcon,    label: 'Meeting', tone: 'text-[#6d28d9] bg-[#f5f3ff] dark:bg-[rgba(109,40,217,0.22)] dark:text-[#c4b5fd]' },
  task:    { icon: CheckSquareIcon, label: 'Task',    tone: 'text-[#1d4ed8] bg-[#eff6ff] dark:bg-[rgba(29,78,216,0.2)] dark:text-[#93bdfb]' },
  deal:    { icon: DealFlowIcon,    label: 'Deal',    tone: 'text-good bg-[rgba(34,197,94,0.14)]' },
}

// The default-pipeline stage ids HubSpot ships with are single lowercase words,
// so the generic enum humaniser can only capitalise them and "Presentation
// scheduled" comes out as "Presentationscheduled". These are the standard set;
// anything custom falls through to the humaniser untouched.
const DEAL_STAGE = {
  appointmentscheduled: 'Appointment scheduled',
  qualifiedtobuy: 'Qualified to buy',
  presentationscheduled: 'Presentation scheduled',
  decisionmakerboughtin: 'Decision maker bought in',
  contractsent: 'Contract sent',
  closedwon: 'Closed won',
  closedlost: 'Closed lost',
}
const stageLabel = (v) => DEAL_STAGE[clean(v).toLowerCase()] || humanizeEnum(v)

// A status reads as a label, not a shout: HubSpot stores NOT_STARTED / WAITING,
// and humanizeEnum keeps multi-word values upper ("NOT STARTED").
const statusLabel = (v) => {
  const t = humanizeEnum(v)
  return t && t === t.toUpperCase() ? t.charAt(0) + t.slice(1).toLowerCase() : t
}

// The route answers with a chat digest by default (title/summary) and the whole
// record when asked for detail (subject/body). The view reads either, so a
// deploy-order mismatch degrades to shorter text instead of empty cards.
const titleOf = (item) => clean(item.subject) || clean(item.title) || clean(item.name)
const bodyOf = (item) => plainText(item.body) || plainText(item.description) || plainText(item.summary)


// ── the mailbox lane ────────────────────────────────────────────────────────
//
// Gmail is asked about THIS company only: every address the lead carries, plus
// its own domain, in both directions. The result is merged into the mirror's
// timeline — and anything the mirror already holds is dropped rather than shown
// twice, matched on the calendar day and direction (the mirror stores no RFC
// message id, so the day is the strongest honest key).
async function gmailForLead(lead) {
  const addrs = [...rowAddresses(lead)]
  const dom = normDomain(lead?.website)
  const targets = [...new Set([...addrs, dom].filter(Boolean))]
  if (!targets.length) return { checked: false, items: [] }
  const clause = targets.map((t) => (t.includes('@') ? t : t)).join(' OR ')
  try {
    const [sent, got] = await Promise.all([
      gmailMessages({ q: `in:sent {${clause}}`, max: 25 }).catch(() => null),
      gmailMessages({ q: `{${clause}} -in:sent`, max: 25 }).catch(() => null),
    ])
    if (!sent && !got) return { checked: false, items: [] }
    const shape = (m, direction) => ({
      id: `gmail-${m.id}`,
      object_type: 'email',
      kind: 'email',
      name: m.subject || 'Email',
      subject: m.subject || '',
      body: m.snippet || '',
      direction,
      from: m.from || '',
      to: m.to || '',
      occurred_at: m.date || '',
      created_at: m.date || '',
      hubspot_url: '',
      _gmail: true,
    })
    return {
      checked: true,
      address: (sent || got)?.address || '',
      items: [
        ...((sent?.messages || []).map((m) => shape(m, 'OUTGOING'))),
        ...((got?.messages || []).map((m) => shape(m, 'INCOMING'))),
      ],
    }
  } catch {
    return { checked: false, items: [] }
  }
}

const dayKey = (v) => {
  const d = new Date(v || '')
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

function mergeMailbox(mirror, mailbox) {
  const seen = new Set()
  for (const it of mirror) {
    const k = kindOf(it)
    if (k !== 'email') continue
    seen.add(`${dayKey(it.occurred_at || it.created_at)}|${String(it.direction || '').toUpperCase().includes('INCOMING') ? 'in' : 'out'}`)
  }
  const extra = []
  for (const m of mailbox) {
    const key = `${dayKey(m.occurred_at)}|${m.direction === 'INCOMING' ? 'in' : 'out'}`
    // Already in the CRM — the mirror's copy wins, it carries the logged body.
    if (seen.has(key)) continue
    seen.add(key)
    extra.push({ ...m, _gmailOnly: true })
  }
  return [...mirror, ...extra]
}

// The order the chips read in: what was said first, then what was done.
const CHIP_ORDER = ['email', 'note', 'call', 'meeting', 'task', 'deal']

function kindOf(item) {
  const t = clean(item.object_type)
  if (t === 'task' || t === 'deal') return t
  return clean(item.kind) || t || 'note'
}

// One line saying which way an email went, from the mirror's own direction
// field. Absent when the mirror did not record one — never guessed from the
// address, because a thread with several senders would guess wrong.
function directionOf(item) {
  const d = clean(item.direction).toUpperCase()
  if (d.includes('INCOMING') || d === 'INBOUND') return 'in'
  if (d.includes('OUTGOING') || d.includes('EMAIL') || d === 'OUTBOUND') return 'out'
  return ''
}

function Entry({ item, defaultOpen }) {
  const [open, setOpen] = useState(Boolean(defaultOpen))
  const kind = kindOf(item)
  const meta = KIND[kind] || KIND.note
  const Icon = meta.icon
  const dir = kind === 'email' ? directionOf(item) : ''
  const title = titleOf(item) || (kind === 'deal' ? 'Deal' : kind === 'task' ? 'Task' : meta.label)
  const body = bodyOf(item)
  const when = formatDayTime(momentSourceOf(item)) || ''
  const undated = momentOf(item) === null

  // The one-line summary under the title, per kind — only facts the record
  // carries. A field the mirror left empty writes no clause.
  const facts = []
  if (kind === 'email') {
    if (clean(item.from)) facts.push(`from ${clean(item.from)}`)
    if (clean(item.to)) facts.push(`to ${clean(item.to)}`)
    // NOT the status on an inbound email. HubSpot stamps hs_email_status SENT
    // on a logged incoming message too (it describes the logged send, not the
    // direction), so printing it put "Sent" on a card headed RECEIVED. The
    // direction is the fact that matters; the status only adds anything to a
    // message we sent.
    if (dir !== 'in' && clean(item.status)) facts.push(statusLabel(item.status))
  } else if (kind === 'call') {
    if (clean(item.disposition)) facts.push(statusLabel(item.disposition))
    const ms = Number(item.duration_ms)
    if (Number.isFinite(ms) && ms > 0) facts.push(`${Math.round(ms / 60000)} min`)
  } else if (kind === 'task') {
    if (clean(item.status)) facts.push(statusLabel(item.status))
    if (clean(item.priority)) facts.push(`${humanizeEnum(item.priority)} priority`)
    if (clean(item.due_at)) facts.push(`due ${formatDayTime(item.due_at)}`)
  } else if (kind === 'deal') {
    if (clean(item.stage)) facts.push(stageLabel(item.stage))
    if (clean(item.amount)) facts.push(`$${Number(item.amount).toLocaleString()}`)
    if (item.is_closed) facts.push(item.is_won ? 'won' : 'closed')
  }

  // ONE CARD PER EVENT, the same geometry as the lead cards a run posts into
  // the chat: a bordered panel, an ordinal-free header strip naming what this
  // is and when, then the content in the email font at reading size. The thin
  // rail this replaced showed a subject and two lines of grey — which is a list
  // of things that happened, not a record you can actually read.
  //
  // SENT AND RECEIVED READ DIFFERENTLY. A reply is the most valuable thing on
  // the page, so an inbound email gets the accent border and tint; everything
  // Bo sent stays neutral. That is the back-and-forth, visible at a glance.
  const inbound = dir === 'in'
  return (
    <article
        className={`overflow-hidden rounded-[14px] ${
          inbound
            ? 'bg-[rgba(60,135,247,0.06)] [border:1px_solid_var(--primary)]'
            : 'bg-surface [border:1px_solid_var(--border)]'
        }`}
      >
        <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-[18px] py-[11px] [border-bottom:1px_solid_var(--border)]">
          <span className={`grid h-[24px] w-[24px] flex-none place-items-center rounded-[7px] ${meta.tone} [&_svg]:h-[13px] [&_svg]:w-[13px]`}>
            <Icon aria-hidden="true" />
          </span>
          <span className="text-[12px] font-bold uppercase tracking-[0.06em] text-dim">
            {meta.label}
            {dir === 'in' && ' · received'}
            {dir === 'out' && ' · sent'}
          </span>
          {when && <span className="text-[12.5px] text-faint">{when}</span>}
          {!when && undated && <span className="text-[12.5px] italic text-faint">no date recorded</span>}
          {/* FROM THE MAILBOX, NOT THE CRM. This message is real — it is in the
              sent folder — but nobody logged it to HubSpot, so it has no record
              to open and the CRM's own counts do not include it. Saying so is
              the difference between evidence and a claim. */}
          {item._gmailOnly && (
            <span
              className="rounded-full bg-[#fefce8] px-[8px] py-[2px] text-[11px] font-semibold text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]"
              title="Found in the connected mailbox. It was never logged to HubSpot, so the CRM does not count it."
            >
              Gmail · not in HubSpot
            </span>
          )}
          {/* LOGGED ON ANOTHER RECORD. This item is in the CRM, but on a
              different company record than the one this lead matched — the
              header note above names the record and links it. */}
          {item._recordLabel && (
            <span
              className="rounded-full bg-panel px-[8px] py-[2px] text-[11px] font-semibold text-dim"
              title="Logged in HubSpot on a different company record than the one this lead matched — see the note at the top."
            >
              on {item._recordLabel}
            </span>
          )}
          {item.hubspot_url && (
            <a
              href={item.hubspot_url}
              target="_blank"
              rel="noreferrer noopener"
              className="ml-auto flex-none text-[12.5px] text-faint no-underline transition-colors hover:text-ink hover:underline"
            >
              Open in HubSpot ↗
            </a>
          )}
        </header>

        <div className="px-[18px] py-[14px]">
          <h2 className="m-0 break-words text-[17px] font-semibold leading-[1.35] text-ink">{title}</h2>
          {facts.length > 0 && (
            <p className="m-0 mt-[5px] break-words text-[13px] text-dim">{facts.join(' · ')}</p>
          )}
          {body && (
            <>
              {/* The email itself, in the email font at the size it was written
                  — the whole point of opening the thread. */}
              <div
                className={`mt-[12px] whitespace-pre-line break-words text-[14.5px] leading-[1.6] text-ink ${
                  open ? '' : 'max-h-[190px] overflow-hidden'
                }`}
                style={EMAIL_FONT}
              >
                {body}
              </div>
              {body.length > 420 && (
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className="mt-[10px] cursor-pointer rounded-[8px] bg-panel px-[11px] py-[6px] text-[12.5px] font-semibold text-dim transition-colors hover:text-ink [border:none]"
                >
                  {open ? 'Show less' : 'Show the rest'}
                </button>
              )}
            </>
          )}
          {!body && kind === 'email' && (
            <p className="m-0 mt-[10px] text-[13px] italic text-faint">
              The mirror holds no body for this email — open it in HubSpot to read it.
            </p>
          )}
        </div>
    </article>
  )
}

/**
 * @param {object} lead        the receipt stub — { key, name, email }
 * @param {string} companyId   the MATCHED HubSpot company id, or '' when the
 *                             lead never resolved to a mirrored record
 * @param {string} hubspotUrl  that record's URL, for the header link
 * @param {function} onBack
 * @param {string} backLabel   the Back button's words where "Back" alone would
 *                             not say where it goes (Activity: "Back to responses")
 * @param {boolean} intro      false leaves out the paragraph under the title -
 *                             Activity opens it bare, like its other pages
 */
export default function LeadTimelineView({ lead, companyId, hubspotUrl = '', onBack, backLabel = '', intro = true }) {
  const [state, setState] = useState({ status: 'loading', items: [], truncated: false, reason: '' })
  // Which kinds are shown. EMPTY MEANS EVERYTHING — the thread opens whole, and
  // a filter is something the reader turns ON, never a default that hides
  // history without saying so. Multi-select: emails and notes together is the
  // common read ("what was said"), tasks and deals the other ("what was done").
  const [kinds, setKinds] = useState([])

  // WHO THIS LEAD IS, ON THE WIRE. The matched record is one way to find its
  // history; the lead's own addresses are the other, and the one the cadence
  // itself counts touches by. Both go to the mirror, which reads every record
  // the lead's contacts and domain are logged on — not only the one record the
  // matcher happened to settle on.
  //
  // THE BALL CONSTRUCTION BUG (2026-09-11). HubSpot held two company records
  // for one company: the import's own empty "Ball Construction", and an
  // auto-created "Ballcc" (domain ballcc.com) carrying the contact and every
  // one of Bo's four logged emails. The card matched the empty one, this view
  // read by that id alone, and a lead the follow-up engine had just marked
  // "Touch 2" opened to "Nothing on this company yet" in front of Bo. The
  // touch number and the thread are now read the same way — by the lead —
  // so they cannot disagree again; and a record the mail was found on other
  // than the matched one is named, so the duplicate can be merged in HubSpot.
  const addressKey = useMemo(
    () => [...rowAddresses(lead)].filter((a) => a.includes('@')).slice(0, 8).join(','),
    [lead],
  )
  const leadDomain = normDomain(lead?.website || lead?.domain || '')

  useEffect(() => {
    let alive = true
    if (!companyId && !addressKey) {
      setState({ status: 'unmatched', items: [], truncated: false, reason: '' })
      return undefined
    }
    setState({ status: 'loading', items: [], truncated: false, reason: '' })
    setKinds([])
    // TWO SOURCES, because the CRM only holds what somebody logged to it. Bo
    // sends plenty straight from Gmail, and a thread page that reads the mirror
    // alone shows an empty history for a company he has emailed four times.
    // The mailbox lane is additive and clearly marked: it can prove a message
    // existed and when, never what the CRM says about it.
    Promise.all([
      hubspotActivity({
        ...(companyId ? { company_id: companyId } : {}),
        ...(addressKey ? { email: addressKey } : {}),
        ...(leadDomain ? { domain: leadDomain } : {}),
        limit: LIMIT,
        detail: 1,
      }),
      gmailForLead(lead),
    ])
      .then(([res, gmail]) => {
        if (!alive) return
        // `available:false` is the mirror saying it could not answer — a
        // different fact from "this company has no history", and the two must
        // never render the same way.
        if (res && res.available === false && !(gmail.items || []).length) {
          setState({ status: 'error', items: [], truncated: false, reason: res.reason || 'The HubSpot mirror could not be reached.' })
          return
        }
        // WHICH RECORD EACH ITEM WAS LOGGED ON. The route names every record
        // it read (`records`: the matched one, then any the lead's domain or
        // contacts led to) and stamps each item with the id of the one that
        // supplied it. An item from a record OTHER than the matched one wears
        // that record's name, so the reader can see the split history and fix
        // it where it lives — in HubSpot, by merging the duplicates.
        const records = Array.isArray(res?.records) ? res.records : []
        const named = new Map(records.map((r) => [String(r.id || ''), r]))
        const mirror = (Array.isArray(res?.activity) ? res.activity : []).map((it) => {
          const rec = named.get(String(it.company_id || ''))
          const other = rec && String(rec.via || '') !== 'match' && String(rec.id || '') !== String(companyId || '')
          return other ? { ...it, _recordLabel: rec.name || rec.domain || `record ${rec.id}` } : it
        })
        const items = mergeMailbox(mirror, gmail.items || [])
        setState({
          status: 'ready',
          items,
          truncated: Boolean(res?.truncated),
          reason: '',
          records,
          mailbox: gmail.checked ? { address: gmail.address, added: items.filter((i) => i._gmailOnly).length } : null,
        })
      })
      .catch((err) => {
        if (alive) setState({ status: 'error', items: [], truncated: false, reason: err?.message || 'The HubSpot mirror could not be reached.' })
      })
    return () => {
      alive = false
    }
  }, [companyId, addressKey, leadDomain])

  // The records the mail turned up on that are NOT the matched one — the
  // duplicates a merge in HubSpot would fold together. Only records that
  // actually supplied items are worth a sentence.
  const otherRecords = useMemo(
    () =>
      (Array.isArray(state.records) ? state.records : []).filter(
        (r) => String(r.via || '') !== 'match' && String(r.id || '') !== String(companyId || '') && Number(r.items) > 0,
      ),
    [state.records, companyId],
  )
  const fromOtherRecords = useMemo(
    () => state.items.filter((it) => it._recordLabel).length,
    [state.items],
  )
  // What the follow-up run itself counted on this lead (chat.js#leadStub
  // carries the run's own read of the mirror, dated and directed). Shown
  // ONLY when the mirror returns nothing now — the one case where the card
  // and this page would otherwise contradict each other on screen.
  const runMail = useMemo(
    () => (Array.isArray(lead?.history) ? lead.history.filter((m) => m && m.at) : []),
    [lead],
  )

  // Oldest first: this is the story of the relationship, and a story reads
  // forwards. The newest entry is the one Bo is about to follow up on, so it
  // sits at the bottom, expanded.
  const ordered = useMemo(
    () =>
      [...state.items].sort((a, b) => {
        const x = momentOf(a)
        const y = momentOf(b)
        if (x === null && y === null) return 0
        if (x === null) return 1
        if (y === null) return -1
        return x - y
      }),
    [state.items],
  )

  const counts = useMemo(() => {
    const c = {}
    for (const it of ordered) {
      const k = kindOf(it)
      c[k] = (c[k] || 0) + 1
    }
    return c
  }, [ordered])

  // Only the kinds this company actually has get a chip: a "Deals" filter on a
  // thread with no deal is a control that can only ever empty the page.
  const chips = useMemo(
    () => CHIP_ORDER.filter((k) => counts[k]).map((k) => ({ kind: k, count: counts[k] })),
    [counts],
  )
  const shown = useMemo(
    () => (kinds.length ? ordered.filter((it) => kinds.includes(kindOf(it))) : ordered),
    [ordered, kinds],
  )

  // NOTE (2026-08-25): a banner explaining all-outbound threads ("this portal
  // logged no inbound before Jan 22, 2026 …") lived here for a day and was
  // removed at the user's request — the fact is real (verified against the
  // whole mirror, 2,801 engagements) but it is operator knowledge, not
  // something a rep needs over every quiet thread. It stays recorded in the
  // project memory; do not resurrect it here without being asked.
  const toggleKind = (k) =>
    setKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]))

  return (
    <section className="flex h-full min-h-0 flex-col">
      <header className="mx-auto w-full max-w-[760px] flex-none">
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel || 'Back to chat'}
          className="inline-flex cursor-pointer items-center gap-x-2 rounded-[8px] bg-transparent px-1 py-1 text-[13px] font-medium text-dim transition-colors hover:text-ink [border:none]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>{backLabel || 'Back'}</span>
        </button>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="m-0 min-w-0 flex-shrink truncate text-[21px] font-bold tracking-[-0.02em] text-ink">
            {lead?.name || 'This lead'}
          </h1>
          {hubspotUrl && (
            <a
              href={hubspotUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex flex-none items-center rounded-[9px] border border-line bg-surface px-[10px] py-[5px] text-[12.5px] font-semibold text-ink no-underline transition-colors hover:bg-panel"
            >
              Open in HubSpot ↗
            </a>
          )}
        </div>
        {intro && (
          <p className="m-0 mt-1 text-[13px] leading-[1.55] text-dim">
            Everything your CRM holds on this company, oldest first — emails both ways, notes,
            calls, meetings, tasks and deals — plus any email in your connected mailbox that never
            made it into HubSpot. Both are read-only; nothing here changes anything.
          </p>
        )}
        {/* THE SPLIT HISTORY, NAMED. Part of this thread was logged on a
            HubSpot record other than the one this lead matched — a duplicate
            company (HubSpot auto-creates one per email domain when a contact is
            added), or the contact's other association. The reader sees which,
            with the link, because the fix is a merge in HubSpot, not here. */}
        {state.status === 'ready' && otherRecords.length > 0 && (
          <div className="mt-3 rounded-[10px] border border-line bg-panel px-3 py-2 text-[12.5px] leading-[1.55] text-dim">
            <span className="font-semibold text-ink">
              {companyId
                ? `${fromOtherRecords} of these ${fromOtherRecords === 1 ? 'is' : 'are'} logged on ${
                    otherRecords.length === 1 ? 'another HubSpot record' : `${otherRecords.length} other HubSpot records`
                  } for this company: `
                : `No company record matched this lead; this history was found through its contact address on: `}
            </span>
            {otherRecords.map((r, i) => (
              <span key={r.id || i}>
                {i > 0 && ', '}
                {r.hubspot_url ? (
                  <a href={r.hubspot_url} target="_blank" rel="noreferrer noopener" className="text-primary-dark no-underline hover:underline">
                    {r.name || r.domain || `record ${r.id}`}
                    {r.domain && r.name ? ` (${r.domain})` : ''} ↗
                  </a>
                ) : (
                  <span className="text-ink">{r.name || r.domain || `record ${r.id}`}</span>
                )}
              </span>
            ))}
            {companyId
              ? '. HubSpot holds more than one record for this company, so the matched record alone tells only part of the story — merge them in HubSpot to keep one history.'
              : '.'}
          </div>
        )}
        {state.status === 'ready' && chips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {chips.map(({ kind, count }) => {
              const on = kinds.includes(kind)
              const K = KIND[kind] || KIND.note
              const ChipIcon = K.icon
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() => toggleKind(kind)}
                  aria-pressed={on}
                  className={`inline-flex cursor-pointer items-center gap-x-[7px] rounded-full px-[11px] py-[6px] text-[12.5px] font-semibold transition-colors ${
                    on
                      ? 'bg-primary text-white [border:1px_solid_var(--primary)]'
                      : 'bg-surface text-dim hover:text-ink [border:1px_solid_var(--border)]'
                  }`}
                >
                  <ChipIcon aria-hidden="true" className="h-[13px] w-[13px]" />
                  {K.label}
                  {count === 1 ? '' : 's'}
                  <span className={`tabular-nums ${on ? 'text-white/75' : 'text-faint'}`}>{count}</span>
                </button>
              )
            })}
            {kinds.length > 0 && (
              <button
                type="button"
                onClick={() => setKinds([])}
                className="cursor-pointer bg-transparent px-1 text-[12.5px] font-semibold text-dim transition-colors hover:text-ink [border:none]"
              >
                Show all
              </button>
            )}
          </div>
        )}
      </header>

      <div className="no-scrollbar mt-4 min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[760px]">
        {state.status === 'loading' && (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-y-3 rounded-[12px] border border-line bg-surface">
            <span
              className="h-7 w-7 animate-spin rounded-full [border:2px_solid_var(--table-line)] [border-top-color:var(--primary)]"
              role="status"
              aria-label="Loading the thread"
            />
            <span className="text-[13px] text-dim">Reading the HubSpot mirror…</span>
          </div>
        )}

        {state.status === 'unmatched' && (
          <div className="rounded-[12px] border border-line bg-surface p-10 text-center">
            <div className="text-[15px] font-semibold text-ink">No HubSpot record matched this lead.</div>
            <p className="mx-auto mt-2 max-w-[460px] text-[13.5px] leading-[1.6] text-dim">
              There is no company in the mirror to read a history from, and the lead carries no
              email address to look one up by — which is not the same as “no history”. Most new
              companies simply are not in HubSpot yet. Search the portal for it from the card, and
              this thread fills in once the record exists and the mirror syncs.
            </p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="rounded-[12px] border border-line bg-surface p-10 text-center">
            <div className="text-[15px] font-semibold text-danger">The thread could not be read.</div>
            <p className="mx-auto mt-2 max-w-[460px] text-[13.5px] leading-[1.6] text-dim">
              {state.reason} Nothing is shown rather than a partial history that might read as the
              whole story.
            </p>
          </div>
        )}

        {state.status === 'ready' && ordered.length === 0 && (
          <div className="rounded-[12px] border border-line bg-surface p-10 text-center">
            <div className="text-[15px] font-semibold text-ink">
              {companyId ? 'Nothing on this company yet.' : 'Nothing logged for this lead yet.'}
            </div>
            <p className="mx-auto mt-2 max-w-[460px] text-[13.5px] leading-[1.6] text-dim">
              {companyId
                ? 'The record exists in HubSpot, and the mirror holds no email, note, call, meeting, task or deal attached to it'
                : 'No company record matched this lead, and the mirror holds no email logged to'}
              {addressKey
                ? `${companyId ? ' — nor to any record its ' : ' its '}${addressKey.includes(',') ? 'addresses' : 'address'} (${addressKey.split(',').join(', ')}) ${companyId ? 'belong to' : 'or to a company on that domain'}`
                : ''}
              .
            </p>
          </div>
        )}

        {/* THE RUN'S OWN EVIDENCE, when the mirror now returns none of it. The
            card said "Touch N" because the run read these messages off the
            mirror; if the mirror answers nothing today the reader is owed the
            messages the number rests on — dated and directed as the run
            recorded them — and told plainly that the two disagree. */}
        {state.status === 'ready' && ordered.length === 0 && runMail.length > 0 && (
          <div className="mt-3 rounded-[12px] border border-warn bg-surface px-5 py-4">
            <div className="text-[13.5px] font-semibold text-warn">
              The follow-up run counted {runMail.length} {runMail.length === 1 ? 'message' : 'messages'} on this lead that the mirror does not return now.
            </div>
            <p className="m-0 mt-1 text-[12.5px] leading-[1.55] text-dim">
              Shown from the run’s own read of the mirror at the time. The mirror may have re-synced since, or the record they were logged on may have been deleted or merged in HubSpot.
            </p>
            <ol className="m-0 mt-2.5 flex list-none flex-col gap-y-1.5 border-l border-line pl-3">
              {runMail.map((m, i) => (
                <li key={`${m.at}-${i}`} className="text-[12.5px] leading-[1.45]">
                  <span className="font-mono text-faint">{formatDayTime(m.at) || 'undated'}</span>
                  <span className="text-faint"> · </span>
                  <span className={m.dir === 'in' ? 'font-semibold text-good' : 'font-semibold text-dim'}>
                    {m.dir === 'in' ? 'they replied' : 'we wrote'}
                  </span>
                  {m.bounced && <span className="text-warn"> · bounced</span>}
                  {m.auto && <span className="text-faint"> · auto-reply</span>}
                  {m.subject && (
                    <>
                      <span className="text-faint"> · </span>
                      <span className="text-ink">{m.subject}</span>
                    </>
                  )}
                  {m.snippet && <div className="mt-0.5 text-faint">“{m.snippet}”</div>}
                </li>
              ))}
            </ol>
          </div>
        )}

        {state.status === 'ready' && ordered.length > 0 && (
          <>
            {state.truncated && (
              <p className="m-0 mb-3 rounded-[10px] border border-line bg-panel px-3 py-2 text-[12.5px] leading-[1.5] text-dim">
                This company has more history than one read returns — the {LIMIT} most recent items
                are shown, so the oldest end of the story is missing.
              </p>
            )}
            {shown.length === 0 ? (
              <div className="rounded-[12px] border border-line bg-surface p-8 text-center">
                <div className="text-[14px] font-semibold text-ink">Nothing of that kind on this company.</div>
                <button
                  type="button"
                  onClick={() => setKinds([])}
                  className="mt-2 cursor-pointer bg-transparent text-[13px] font-semibold text-primary-dark [border:none] hover:underline"
                >
                  Show everything
                </button>
              </div>
            ) : (
              <ol className="m-0 flex list-none flex-col p-0">
                {shown.map((item, i) => (
                  <li key={`${item.object_type}-${item.id}-${i}`} className="list-none">
                    {/* THE FLOW LINE. A dotted rail in the gap, so the cards read
                        as one chronology rather than a stack of unrelated boxes —
                        and dotted rather than solid because time passed here, an
                        unknown amount of it. */}
                    {i > 0 && (
                      <div
                        aria-hidden="true"
                        className="mx-auto h-[34px] w-0 [border-left:2px_dotted_var(--border)]"
                      />
                    )}
                    <Entry item={item} defaultOpen={i === shown.length - 1} />
                  </li>
                ))}
              </ol>
            )}
            <p className="m-0 mt-4 flex items-center gap-x-2 text-[12px] text-faint">
              <ClockIcon className="h-[13px] w-[13px]" />
              Bodies are stored trimmed in the mirror — open an item in HubSpot for the full text.
            </p>
          </>
        )}
        </div>
      </div>
    </section>
  )
}
